import type { NumberFormatOptions } from 'next-intl'
import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { useNumberFormatter } from './number'
import { type FormatterInputScalar, combineOptions } from './types'

type PercentOptions = NumberFormatOptions & {
  invalidStr?: string
  normalized?: boolean
  export?: boolean
}

const defaultOptions = {
  invalidStr: emDash,
  normalized: false,
  export: false
}

const exportOptions = { ...defaultOptions, invalidStr: '' }

// eslint-disable-next-line import/prefer-default-export
export const usePercentFormatter = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value: FormatterInputScalar, options?: PercentOptions) => {
      const {
        value: percent,
        normalized,
        invalidStr,
        ...rest
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (percent === undefined || percent === null) return invalidStr

      const valueToFormat = typeof percent === 'string' ? parseFloat(percent) : percent
      if (isNaN(valueToFormat)) return invalidStr

      // When exporting, do not use the `percent` style, as it will include the `%` symbol. As a
      // result, we may need to "de-normalize" the value.
      if (rest.export) {
        const denormalized = normalized ? valueToFormat * 100 : valueToFormat
        return formatNumber(denormalized, { invalidStr, ...rest })
      }

      const valNormalized = normalized ? valueToFormat : valueToFormat / 100
      return formatNumber(valNormalized, { style: 'percent', invalidStr, ...rest })
    },
    [formatNumber]
  )
}
